import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { headerBranchId } from '../food-delivery/helpers';
import { branchService } from '@/data/branchService';
import { companyProductService } from '@/data/companyProductService';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import {
  EXTERNAL_MAPPING_SOURCE_LABELS,
  ExternalMappingSource,
  ExternalModifierMapping,
  ExternalProductMapping,
  FoodIntegration,
  FOOD_PROVIDER_LABELS,
  ProductMappingSuggestion
} from '@/types/FoodDelivery.interface';
import FoodDeliveryEmptyState from '../food-delivery/components/FoodDeliveryEmptyState';
import { Branch } from '@/types/Branch.interface';
import { CompanyProduct } from '@/types/CompanyProduct.interface';
import { ProductAttribute } from '@/types/ProductAttribute.interface';

type ProductOption = { id: number; name: string; basePrice: number };

// Bir core ürünün düzleştirilmiş seçenekleri (attribute → option).
type OptionChoice = {
  attributeOptionId: number;
  label: string;
  extraPrice: number;
};

const confidenceColor = (c: number): 'success' | 'warning' | 'default' => {
  if (c >= 0.9) return 'success';
  if (c >= 0.5) return 'warning';
  return 'default';
};

const FoodDeliveryMappings: React.FC = () => {
  const { isBranchAdmin, isAdminView, currentBranch } = useUserViewMode();

  const [branches, setBranches] = useState<Branch[]>([]);
  const headerBranch = headerBranchId(currentBranch, isAdminView);
  const [branchId, setBranchId] = useState<number | null>(headerBranch);
  // Üst menüden şube değişince sayfa da o şubeye geçer.
  useEffect(() => {
    if (headerBranch) setBranchId(headerBranch);
  }, [headerBranch]);
  const [integrations, setIntegrations] = useState<FoodIntegration[]>([]);
  const [integrationId, setIntegrationId] = useState<number | null>(null);
  const [products, setProducts] = useState<ProductOption[]>([]);

  const [suggestions, setSuggestions] = useState<ProductMappingSuggestion[]>(
    []
  );
  const [picked, setPicked] = useState<Record<string, ProductOption | null>>(
    {}
  );
  const [mappings, setMappings] = useState<ExternalProductMapping[]>([]);
  const [modifierMappings, setModifierMappings] = useState<
    ExternalModifierMapping[]
  >([]);

  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // Modifier eşleşmesi yaratma formu durumu.
  const [modExternalId, setModExternalId] = useState('');
  const [modExternalName, setModExternalName] = useState('');
  const [modProduct, setModProduct] = useState<ProductOption | null>(null);
  const [modOptions, setModOptions] = useState<OptionChoice[]>([]);
  const [modOption, setModOption] = useState<OptionChoice | null>(null);
  const [modLoadingOpts, setModLoadingOpts] = useState(false);
  const modifierOptionsRequestId = useRef(0);
  const [modSaving, setModSaving] = useState(false);
  const [toast, setToast] = useState<{
    msg: string;
    severity: 'success' | 'error' | 'info';
  } | null>(null);

  // Şube listesi (admin/şirket görünümü).
  useEffect(() => {
    if (isBranchAdmin) return;
    branchService
      .getAllFlat({ getAll: true })
      .then(setBranches)
      .catch(() =>
        setToast({ msg: 'Şubeler getirilemedi.', severity: 'error' })
      );
  }, [isBranchAdmin]);

  // Şirket ürünleri (eşleştirme adayları için tam liste).
  useEffect(() => {
    companyProductService
      .getAllFlat({ getAll: true })
      .then((list: CompanyProduct[]) =>
        setProducts(
          list.map((p) => ({
            id: p.id,
            name: p.name,
            basePrice: p.basePrice
          }))
        )
      )
      .catch(() =>
        setToast({ msg: 'Ürünler getirilemedi.', severity: 'error' })
      );
  }, []);

  // Şube seçilince entegrasyonları getir.
  useEffect(() => {
    if (!branchId) {
      setIntegrations([]);
      setIntegrationId(null);
      return;
    }
    foodDeliveryService
      .listByBranch(branchId)
      .then((list) => {
        setIntegrations(list);
        setIntegrationId(list[0]?.id ?? null);
      })
      .catch(() =>
        setToast({ msg: 'Entegrasyonlar getirilemedi.', severity: 'error' })
      );
  }, [branchId]);

  const loadMappings = useCallback(async () => {
    if (!integrationId) {
      setMappings([]);
      setModifierMappings([]);
      return;
    }
    setLoading(true);
    try {
      const [pm, mm] = await Promise.all([
        foodDeliveryService.listMappings(integrationId),
        foodDeliveryService.listModifierMappings(integrationId)
      ]);
      setMappings(pm);
      setModifierMappings(mm);
    } catch {
      setToast({ msg: 'Eşleşmeler getirilemedi.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    setSuggestions([]);
    setPicked({});
    loadMappings();
  }, [loadMappings]);

  const productById = useMemo(
    () => new Map(products.map((p) => [p.id, p])),
    [products]
  );

  const handleImport = async () => {
    if (!integrationId) return;
    setImporting(true);
    try {
      const res = await foodDeliveryService.importMenu(integrationId);
      setSuggestions(res.suggestions);
      // Önerilen en iyi adayı varsayılan seçim olarak doldur.
      const next: Record<string, ProductOption | null> = {};
      for (const s of res.suggestions) {
        const best = s.candidates[0];
        next[s.externalProductId] = best
          ? (productById.get(best.companyProductId) ?? {
              id: best.companyProductId,
              name: best.name,
              basePrice: best.basePrice
            })
          : null;
      }
      setPicked(next);
      setToast({
        msg: `Menü içe aktarıldı: ${res.itemCount} ürün, ${res.suggestions.length} öneri.`,
        severity: 'success'
      });
    } catch {
      setToast({
        msg: 'Menü içe aktarılamadı (provider MENU_PULL desteklemiyor olabilir).',
        severity: 'error'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleConfirm = async (s: ProductMappingSuggestion) => {
    if (!integrationId) return;
    const selected = picked[s.externalProductId];
    if (!selected) {
      setToast({ msg: 'Önce bir ürün seçin.', severity: 'info' });
      return;
    }
    setBusyKey(s.externalProductId);
    try {
      const best = s.candidates[0];
      // Önerilen adaydan değişmediyse SUGGESTED, aksi halde MANUAL.
      const isSuggested = best && best.companyProductId === selected.id;
      await foodDeliveryService.upsertMapping(integrationId, {
        externalProductId: s.externalProductId,
        companyProductId: selected.id,
        externalProductName: s.externalProductName,
        externalCategoryName: s.externalCategoryName ?? undefined,
        confidence: isSuggested ? s.bestConfidence : undefined,
        source: isSuggested
          ? ExternalMappingSource.SUGGESTED
          : ExternalMappingSource.MANUAL
      });
      setToast({ msg: 'Eşleşme kaydedildi.', severity: 'success' });
      // İşlenen öneriyi listeden çıkar + kayıtlı eşleşmeleri tazele.
      setSuggestions((prev) =>
        prev.filter((x) => x.externalProductId !== s.externalProductId)
      );
      await loadMappings();
    } catch {
      setToast({ msg: 'Eşleşme kaydedilemedi.', severity: 'error' });
    } finally {
      setBusyKey(null);
    }
  };

  const handleDeleteMapping = async (m: ExternalProductMapping) => {
    if (!integrationId) return;
    if (!window.confirm(`"${m.externalProductId}" eşleşmesi kaldırılsın mı?`))
      return;
    setBusyKey(`del-${m.id}`);
    try {
      await foodDeliveryService.deleteMapping(integrationId, m.id);
      setToast({ msg: 'Eşleşme kaldırıldı.', severity: 'info' });
      await loadMappings();
    } catch {
      setToast({ msg: 'Kaldırma başarısız.', severity: 'error' });
    } finally {
      setBusyKey(null);
    }
  };

  const handleDeleteModifier = async (m: ExternalModifierMapping) => {
    if (!integrationId) return;
    if (!window.confirm('Modifier eşleşmesi kaldırılsın mı?')) return;
    setBusyKey(`delmod-${m.id}`);
    try {
      await foodDeliveryService.deleteModifierMapping(integrationId, m.id);
      setToast({ msg: 'Modifier eşleşmesi kaldırıldı.', severity: 'info' });
      await loadMappings();
    } catch {
      setToast({ msg: 'Kaldırma başarısız.', severity: 'error' });
    } finally {
      setBusyKey(null);
    }
  };

  // Modifier formu: ürün seçilince o ürünün attribute → option'larını düzleştir.
  const handleModProductChange = async (product: ProductOption | null) => {
    const requestId = ++modifierOptionsRequestId.current;
    setModProduct(product);
    setModOption(null);
    setModOptions([]);
    setModLoadingOpts(Boolean(product));
    if (!product) return;
    try {
      const attributes: ProductAttribute[] =
        await companyProductService.getAttributes(product.id);
      if (requestId !== modifierOptionsRequestId.current) return;
      const choices: OptionChoice[] = [];
      for (const attr of attributes ?? []) {
        for (const opt of attr.options ?? []) {
          choices.push({
            attributeOptionId: opt.id,
            label: `${attr.name} → ${opt.name}${
              opt.extraPrice ? ` (+${opt.extraPrice}₺)` : ''
            }`,
            extraPrice: opt.extraPrice
          });
        }
      }
      setModOptions(choices);
      if (choices.length === 0) {
        setToast({
          msg: 'Bu ürünün seçeneği (AttributeOption) yok.',
          severity: 'info'
        });
      }
    } catch {
      if (requestId === modifierOptionsRequestId.current) {
        setToast({ msg: 'Ürün seçenekleri getirilemedi.', severity: 'error' });
      }
    } finally {
      if (requestId === modifierOptionsRequestId.current) {
        setModLoadingOpts(false);
      }
    }
  };

  const handleAddModifierMapping = async () => {
    if (!integrationId || !modOption || !modExternalId.trim()) {
      setToast({
        msg: 'Modifier ID ve core seçenek zorunlu.',
        severity: 'info'
      });
      return;
    }
    setModSaving(true);
    try {
      await foodDeliveryService.upsertModifierMapping(integrationId, {
        externalModifierId: modExternalId.trim(),
        attributeOptionId: modOption.attributeOptionId,
        externalModifierName: modExternalName.trim() || undefined,
        source: ExternalMappingSource.MANUAL
      });
      setToast({ msg: 'Modifier eşleşmesi eklendi.', severity: 'success' });
      setModExternalId('');
      setModExternalName('');
      setModProduct(null);
      setModOption(null);
      setModOptions([]);
      await loadMappings();
    } catch {
      setToast({ msg: 'Modifier eşleşmesi eklenemedi.', severity: 'error' });
    } finally {
      setModSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageTitleWrapper>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography variant="h3" component="h3" gutterBottom>
              Menü & Ürün Eşleştirme
            </Typography>
            <Typography variant="subtitle2">
              Platform menüsünü içe aktarın ve ürünleri core ürünlere eşleyin.
              Eşleşmeyen kalemler siparişte manuel incelemeye düşer.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={
              importing ? <CircularProgress size={18} /> : <CloudDownloadIcon />
            }
            onClick={handleImport}
            disabled={!integrationId || importing}
          >
            Menü İçe Aktar
          </Button>
        </Box>
      </PageTitleWrapper>

      <Box sx={{ px: 4, pb: 4 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {!isBranchAdmin && (
                <Autocomplete
                  sx={{ minWidth: 280 }}
                  options={branches}
                  getOptionLabel={(b) => b.name}
                  value={branches.find((b) => b.id === branchId) ?? null}
                  onChange={(_e, val) => setBranchId(val?.id ?? null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Şube seç" />
                  )}
                />
              )}
              <Autocomplete
                sx={{ minWidth: 280 }}
                options={integrations}
                getOptionLabel={(i) =>
                  `${FOOD_PROVIDER_LABELS[i.providerCode]}${
                    i.displayName ? ` — ${i.displayName}` : ''
                  }`
                }
                value={integrations.find((i) => i.id === integrationId) ?? null}
                onChange={(_e, val) => setIntegrationId(val?.id ?? null)}
                disabled={!branchId}
                renderInput={(params) => (
                  <TextField {...params} label="Entegrasyon seç" />
                )}
              />
            </Stack>
          </CardContent>
        </Card>

        {!integrationId ? (
          branchId && integrations.length === 0 ? (
            <FoodDeliveryEmptyState
              title="Entegrasyon yok"
              message="Bu şubede yemek platformu entegrasyonu yok. Menü eşleştirmesi için önce bir entegrasyon oluşturun."
            />
          ) : (
            <Alert severity="info">
              Eşleştirme için bir şube ve entegrasyon seçin.
            </Alert>
          )
        ) : (
          <>
            {/* Eşleştirme önerileri (menü import sonrası) */}
            {suggestions.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h4" gutterBottom>
                    Eşleştirme Önerileri ({suggestions.length})
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Platform Ürünü</TableCell>
                          <TableCell>Kategori</TableCell>
                          <TableCell>Fiyat</TableCell>
                          <TableCell>Güven</TableCell>
                          <TableCell sx={{ minWidth: 260 }}>
                            Core Ürün
                          </TableCell>
                          <TableCell align="right">İşlem</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {suggestions.map((s) => (
                          <TableRow key={s.externalProductId} hover>
                            <TableCell>
                              <Typography fontWeight={600}>
                                {s.externalProductName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {s.externalProductId}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {s.externalCategoryName ?? '—'}
                            </TableCell>
                            <TableCell>
                              {s.externalPrice != null
                                ? `${s.externalPrice} ₺`
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {s.alreadyMapped ? (
                                <Chip
                                  size="small"
                                  color="info"
                                  label="Zaten eşli"
                                />
                              ) : s.bestConfidence > 0 ? (
                                <Chip
                                  size="small"
                                  color={confidenceColor(s.bestConfidence)}
                                  label={`%${Math.round(
                                    s.bestConfidence * 100
                                  )}${s.autoSuggest ? ' ✓' : ''}`}
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  color="default"
                                  label="Aday yok"
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                size="small"
                                options={products}
                                getOptionLabel={(p) => p.name}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={picked[s.externalProductId] ?? null}
                                onChange={(_e, val) =>
                                  setPicked((prev) => ({
                                    ...prev,
                                    [s.externalProductId]: val
                                  }))
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Core ürün seç"
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {busyKey === s.externalProductId ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Tooltip title="Eşleşmeyi onayla">
                                  <span>
                                    <IconButton
                                      color="success"
                                      onClick={() => handleConfirm(s)}
                                      disabled={!picked[s.externalProductId]}
                                    >
                                      <CheckIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Kayıtlı ürün eşleşmeleri */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Kayıtlı Ürün Eşleşmeleri ({mappings.length})
                </Typography>
                {loading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : mappings.length === 0 ? (
                  <Alert severity="info">
                    Henüz eşleşme yok. Yukarıdaki{' '}
                    <strong>Menüyü İçe Aktar</strong> düğmesiyle platform
                    menüsünü çekin, ardından önerileri onaylayın. Eşleşmeyen
                    ürünlerin siparişi manuel incelemeye düşer.
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Platform Ürün ID</TableCell>
                          <TableCell>Platform Adı</TableCell>
                          <TableCell>Core Ürün</TableCell>
                          <TableCell>Kaynak</TableCell>
                          <TableCell align="right">İşlem</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mappings.map((m) => (
                          <TableRow key={m.id} hover>
                            <TableCell>{m.externalProductId}</TableCell>
                            <TableCell>
                              {m.externalProductName ?? '—'}
                            </TableCell>
                            <TableCell>
                              {m.companyProduct?.name ??
                                `#${m.companyProductId}`}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={EXTERNAL_MAPPING_SOURCE_LABELS[m.source]}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {busyKey === `del-${m.id}` ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Tooltip title="Eşleşmeyi kaldır">
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => handleDeleteMapping(m)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            {/* Modifier eşleşmeleri */}
            <Card>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Modifier Eşleşmeleri ({modifierMappings.length})
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {/* Modifier eşleşmesi ekleme formu */}
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ md: 'center' }}
                  sx={{ mb: 3 }}
                >
                  <TextField
                    size="small"
                    label="Platform Modifier ID"
                    value={modExternalId}
                    onChange={(e) => setModExternalId(e.target.value)}
                    sx={{ minWidth: 180 }}
                  />
                  <TextField
                    size="small"
                    label="Platform Adı (ops.)"
                    value={modExternalName}
                    onChange={(e) => setModExternalName(e.target.value)}
                    sx={{ minWidth: 160 }}
                  />
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 220 }}
                    options={products}
                    getOptionLabel={(p) => p.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={modProduct}
                    onChange={(_e, val) => handleModProductChange(val)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Core ürün" />
                    )}
                  />
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 260 }}
                    options={modOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) =>
                      o.attributeOptionId === v.attributeOptionId
                    }
                    value={modOption}
                    onChange={(_e, val) => setModOption(val)}
                    loading={modLoadingOpts}
                    disabled={!modProduct || modLoadingOpts}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Core seçenek" />
                    )}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddModifierMapping}
                    disabled={modSaving || !modOption || !modExternalId.trim()}
                    startIcon={
                      modSaving ? <CircularProgress size={16} /> : undefined
                    }
                  >
                    Ekle
                  </Button>
                </Stack>

                {modifierMappings.length === 0 ? (
                  <Alert severity="info">
                    Modifier eşleşmesi yok. (Modifier eşleşmeleri sipariş
                    kalemindeki seçenekleri core seçeneğe [AttributeOption]
                    bağlar.)
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Platform Modifier ID</TableCell>
                          <TableCell>Platform Adı</TableCell>
                          <TableCell>Core Seçenek</TableCell>
                          <TableCell>Kaynak</TableCell>
                          <TableCell align="right">İşlem</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {modifierMappings.map((m) => (
                          <TableRow key={m.id} hover>
                            <TableCell>{m.externalModifierId}</TableCell>
                            <TableCell>
                              {m.externalModifierName ?? '—'}
                            </TableCell>
                            <TableCell>
                              {m.attributeOption
                                ? `${m.attributeOption.attribute.name} → ${m.attributeOption.name}`
                                : `#${m.attributeOptionId}`}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={EXTERNAL_MAPPING_SOURCE_LABELS[m.source]}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {busyKey === `delmod-${m.id}` ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Tooltip title="Modifier eşleşmesini kaldır">
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => handleDeleteModifier(m)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default FoodDeliveryMappings;
